import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FeedContentInspection,
  FeedDetail,
  FeedRequestLog,
  FeedScheduleTable,
  MassDeletionBanner,
  UnreachableFeedBanner,
} from "../components/index.js";
import { useApiClient } from "../api/ApiContext.jsx";
import { SCHEDULE_MODES } from "../api/mappers.js";
import { useApi } from "../hooks/useApi.js";
import { AsyncBoundary, ErrorView, InlineMessage, PageLoading, errorText } from "../ui/Feedback.jsx";

export default function FeedScreen() {
  const [tab, setTab] = useState("overview");

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 20, alignItems: "center" }}>
        <TabButton active={tab === "overview"} onClick={() => setTab("overview")}>
          Overview
        </TabButton>
        <TabButton active={tab === "schedule"} onClick={() => setTab("schedule")}>
          Schedule
        </TabButton>
        <TabButton active={tab === "requests"} onClick={() => setTab("requests")}>
          Request log
        </TabButton>
        {/*
          The calendar content itself. It is a development aid and the
          deployment may have it switched off, so the tab is always offered and
          the refusal is explained inside it — hiding the tab would leave a
          developer wondering whether the feature exists at all.
        */}
        <TabButton active={tab === "content"} onClick={() => setTab("content")}>
          Content
        </TabButton>
      </div>

      {tab === "overview" ? <OverviewTab /> : null}
      {tab === "schedule" ? <ScheduleTab /> : null}
      {tab === "requests" ? <RequestsTab /> : null}
      {tab === "content" ? <ContentTab /> : null}
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      className={`ical-btn${active ? " ical-btn-primary" : ""}`}
      onClick={onClick}
      style={{ padding: "6px 14px", fontSize: "var(--text-sm)" }}
    >
      {children}
    </button>
  );
}

function OverviewTab() {
  const api = useApiClient();
  const navigate = useNavigate();
  const { feedId } = useParams();
  const query = useApi(() => api.feeds.get(feedId), [api, feedId]);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <button type="button" className="ical-btn" onClick={() => navigate(-1)}>
          ← Back
        </button>
      </div>

      <AsyncBoundary query={query} onBack={() => navigate("/")}>
        {(data) => (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {/*
              R1 / R2 banners. `GET /feeds/{id}` does not carry these fields in
              docs/panel-api.md yet (panel-decisions 3.12 proposes
              `feeds.pending_confirmation_at`), so they render from the optional
              fields the mapper reads defensively — no invented endpoint.
            */}
            {data.unreachable ? (
              <UnreachableFeedBanner
                feedId={data.feed.externalId}
                hoursDown={data.unreachableHours ?? 24}
              />
            ) : null}
            {data.massDeletionPending || data.pendingConfirmationAt ? (
              <MassDeletionBanner feedId={data.feed.externalId} />
            ) : null}

            <FeedDetail
              feed={data.feed}
              percentile={data.percentile}
              currentPollRate={data.currentPollRate}
              baselineRate={data.baselineRate}
              diffQuality={data.diffQuality}
              plannedPollsByHour={data.plannedPollsByHour}
              observedPollsByHour={data.observedPollsByHour}
              changedEventsByHour={data.changedEventsByHour}
              histogramTimezone={data.histogramTimezone}
              histogramUtcOffsetMinutes={data.histogramUtcOffsetMinutes}
              recentChanges={data.recentChanges}
            />
          </div>
        )}
      </AsyncBoundary>
    </div>
  );
}

/**
 * Each tab is its own read, so switching to one cannot show data fetched for
 * another question: the schedule is anchored on an instant the poll loop may
 * have moved past, and the request log only exists for the last day.
 *
 * The guard lives inside each tab, not in a wrapper that receives the loaded
 * content as children. JSX children are evaluated when the *parent* renders, so
 * a wrapper's early return cannot protect a child whose props read
 * `query.data.something` — the expression is already evaluated, and a null
 * response throws before the spinner could be shown. That is a blank page, not
 * a loading state.
 */
function TabShell({ children }) {
  const navigate = useNavigate();

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <button type="button" className="ical-btn" onClick={() => navigate(-1)}>
          ← Back
        </button>
      </div>
      {children}
    </div>
  );
}

/** The shared failure branch, so every tab reports a failed read the same way. */
function TabError({ query }) {
  const navigate = useNavigate();

  return (
    <div>
      <InlineMessage tone="danger">{errorText(query.error)}</InlineMessage>
      <ErrorView error={query.error} onRetry={query.reload} onBack={() => navigate("/")} />
    </div>
  );
}

function ScheduleTab() {
  const api = useApiClient();
  const { feedId } = useParams();
  const query = useApi(() => api.feeds.schedule(feedId, { count: 10 }), [api, feedId]);

  if (query.loading) return <PageLoading />;
  if (query.error) return <TabError query={query} />;

  return (
    <TabShell>
      <FeedScheduleTable {...query.data} />
    </TabShell>
  );
}

/**
 * The cached calendar and the removals log. Both are optional server-side: the
 * deployment answers 403 feature_disabled unless it was switched on, and that is
 * a different situation from a failed read, so it gets its own message rather
 * than the shared error view.
 */
function ContentTab() {
  const api = useApiClient();
  const { feedId } = useParams();
  const query = useApi(() => api.feeds.content(feedId, { removals: 50 }), [api, feedId]);

  if (query.loading) return <PageLoading />;
  if (query.error?.isFeatureDisabled) {
    return (
      <TabShell>
        <InlineMessage tone="info">
          {query.error.message} Calendar content inspection is a development and support aid, so it
          is off unless a deployment turns it on.
        </InlineMessage>
      </TabShell>
    );
  }
  if (query.error) return <TabError query={query} />;

  return (
    <TabShell>
      <FeedContentInspection content={query.data} />
    </TabShell>
  );
}

/**
 * The request log needs both reads: the attempts are the trail, and the
 * schedule supplies the interval the "vs plan" column compares against. It
 * fetches both rather than sharing state with the other tab, so a deep link
 * straight to this tab works.
 */
function RequestsTab() {
  const api = useApiClient();
  const { feedId } = useParams();
  const query = useApi(
    async () => {
      const [log, schedule] = await Promise.all([
        api.feeds.polls(feedId, { limit: 50 }),
        api.feeds.schedule(feedId, { count: 1 }),
      ]);
      return { log, schedule };
    },
    [api, feedId],
  );

  if (query.loading) return <PageLoading />;
  if (query.error) return <TabError query={query} />;

  return (
    <TabShell>
      <FeedRequestLog
        attempts={query.data.log.attempts}
        maxAttempts={query.data.log.maxAttempts}
        maxAgeHours={query.data.log.maxAgeHours}
        intervalMinutes={query.data.schedule.mode === SCHEDULE_MODES.fixed ? query.data.schedule.intervalMinutes : null}
      />
    </TabShell>
  );
}
